import { useState, useEffect, useCallback, useRef } from 'react';
import { condoApi } from '../services/api';
import { logger } from '../services/logger';
import { CustomerNote } from './useCustomerNotes';

interface UsePersonNotesResult {
  notes: CustomerNote[];
  loading: boolean;
  error: string | null;
}

/**
 * Simplified hook for fetching person notes WITHOUT auto-refresh
 * Used by PersonNoteBanner to avoid rate limiting
 */
export const usePersonNotes = (customerId: string | undefined, personId: string | undefined): UsePersonNotesResult => {
  const [notes, setNotes] = useState<CustomerNote[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasInitiallyLoaded = useRef(false);

  // Fetch notes for the person (NO auto-refresh to avoid rate limiting)
  const fetchNotes = useCallback(async () => {
    if (!customerId || !personId) {
      setNotes([]);
      return;
    }

    if (!hasInitiallyLoaded.current) {
      setLoading(true);
    }
    setError(null);

    try {
      const filters = { person_id: personId };

      logger.debug('usePersonNotes', 'Fetching notes', { customerId, personId });
      const response = await condoApi.getCustomerNotes(customerId, true, filters);

      if (response.data.success) {
        const newNotes = response.data.notes || [];
        setNotes(newNotes);
        logger.debug('usePersonNotes', 'Notes fetched successfully', {
          customerId,
          personId,
          count: newNotes.length
        });
      } else {
        throw new Error('Failed to fetch notes');
      }
    } catch (err: any) {
      const errorMsg = err.response?.data?.error || err.message || 'Failed to fetch notes';
      logger.error('usePersonNotes', 'Error fetching notes', err, { customerId, personId });
      setError(errorMsg);
    } finally {
      if (!hasInitiallyLoaded.current) {
        hasInitiallyLoaded.current = true;
      }
      setLoading(false);
    }
  }, [customerId, personId]);

  // Fetch notes on mount and when IDs change
  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  // NO auto-refresh to avoid rate limiting

  return {
    notes,
    loading,
    error,
  };
};
